import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from "react";

import { DARK_MODE_ENABLED, THEME, type TTheme } from "./theme.types";

export type TThemeProviderProps = {
  children: ReactNode;
  defaultTheme?: TTheme;
  storageKey?: string;
};

export type TThemeProviderState = {
  theme: TTheme;
  resolvedTheme: "light" | "dark";
  setTheme: (theme: TTheme) => void;
  toggleTheme: () => void;
};

// eslint-disable-next-line react-refresh/only-export-components
export const ThemeProviderContext = createContext<TThemeProviderState | null>(null);

export const ThemeProvider = ({ children, defaultTheme = "system", storageKey = "ui-theme" }: TThemeProviderProps) => {
  const [theme, setTheme] = useState<TTheme>(() => {
    if (!DARK_MODE_ENABLED) return THEME.light;
    return (localStorage.getItem(storageKey) as TTheme) || defaultTheme;
  });

  useEffect(() => {
    const root = window.document.documentElement;
    const effectiveTheme = DARK_MODE_ENABLED ? theme : THEME.light;

    // Disable transitions for one frame so the theme switch is instant
    root.classList.add("no-transitions");

    root.classList.remove("light", "dark");

    if (effectiveTheme === THEME.system) {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
      root.classList.add(systemTheme);
    } else {
      root.classList.add(effectiveTheme);
    }

    const raf = requestAnimationFrame(() => {
      root.classList.remove("no-transitions");
    });
    return () => cancelAnimationFrame(raf);
  }, [theme]);

  const resolvedTheme: "light" | "dark" = (() => {
    const effective = DARK_MODE_ENABLED ? theme : THEME.light;
    if (effective === THEME.system) {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return effective;
  })();

  const value = {
    theme: DARK_MODE_ENABLED ? theme : THEME.light,
    resolvedTheme,
    toggleTheme: useCallback(() => {
      if (!DARK_MODE_ENABLED) return;
      const next = resolvedTheme === THEME.light ? THEME.dark : THEME.light;
      localStorage.setItem(storageKey, next);
      setTheme(next);
    }, [resolvedTheme, storageKey]),
    setTheme: useCallback(
      (nextTheme: TTheme) => {
        if (!DARK_MODE_ENABLED) return;
        localStorage.setItem(storageKey, nextTheme);
        setTheme(nextTheme);
      },
      [storageKey]
    ),
  };

  return <ThemeProviderContext.Provider value={value}>{children}</ThemeProviderContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (!context) throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
